import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { SiteType } from '../../../../graphql/types/SiteType';
import TimeRangeFilter, { type TimeRangeValue } from './components/TimeRangeFilter';
import SortingManagementTab from './components/SortingManagementTab';
import TransportManagementTab from './components/TransportManagementTab';
import { toIsoRange } from './graphql/timeRange';

type TabKey = 'sorting' | 'transport';

// 7 个尾程 hub。分拣型 sub-hub（LAXS / EWRS / LAXSCBT / LAXSTEMU）不在列。
// 取 hub.name 首个 token 与之比对（hub 切换器显示的就是 name，如 "ATL Hub"）。
const TAIL_HUB_LABELS = new Set(['LAX', 'EWR', 'MIA', 'ATL', 'ORD', 'DFW', 'HOU']);

// 分拣型 sub-hub，无运输作业，只展示"仓内作业管理"tab。
const SORTING_ONLY_HUB_LABELS = new Set(['LAXSCBT', 'LAXS', 'LAXSTEMU', 'EWRS']);

const InhubDashboardPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const [activeTabKey, setActiveTabKey] = useState<TabKey>('sorting');
    const [timeRange, setTimeRange] = useState<TimeRangeValue>({
        preset: 'LAST_24H'
    });

    // HUB → 当前 id；WAREHOUSE → parentSiteId（与大包进度页一致）
    const hubId: number | null = useMemo(() => {
        if (!currentWarehouse) return null;
        if (currentWarehouse.siteType === SiteType.HUB) {
            return Number(currentWarehouse.id);
        }
        if (currentWarehouse.siteType === SiteType.WAREHOUSE && currentWarehouse.parentSiteId) {
            return Number(currentWarehouse.parentSiteId);
        }
        return null;
    }, [currentWarehouse]);

    const hubFirstToken = useMemo(() => {
        if (!currentWarehouse || currentWarehouse.siteType !== SiteType.HUB) return '';
        return (currentWarehouse.name ?? '').trim().split(/\s+/)[0];
    }, [currentWarehouse]);

    // 仅尾程 hub 才展示装包量"按目的地展开"下钻；分拣型 sub-hub 的包牌流向（如 LAX2）不适用此下钻。
    const isTailHub = useMemo(() => TAIL_HUB_LABELS.has(hubFirstToken), [hubFirstToken]);

    // 分拣型 sub-hub 隐藏"运输作业管理"tab
    const isSortingOnlyHub = useMemo(() => SORTING_ONLY_HUB_LABELS.has(hubFirstToken), [hubFirstToken]);

    useEffect(() => {
        if (isSortingOnlyHub && activeTabKey === 'transport') {
            setActiveTabKey('sorting');
        }
    }, [isSortingOnlyHub, activeTabKey]);

    const { rangeStart, rangeEnd } = useMemo(() => toIsoRange(timeRange), [timeRange]);

    const items: TabsProps['items'] = [
        {
            key: 'sorting',
            label: t('wms.delivery.inhubDashboard.tabs.sorting')
        },
        ...(isSortingOnlyHub ? [] : [{
            key: 'transport',
            label: t('wms.delivery.inhubDashboard.tabs.transport')
        }])
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <Tabs
                activeKey={activeTabKey}
                onChange={(k) => setActiveTabKey(k as TabKey)}
                items={items}
                className="[&_.ant-tabs-tab]:font-bold"
            />

            <div className="mb-4">
                <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
                <p className="text-black mt-2 mb-0">
                    ⏰ {t('wms.delivery.inhubDashboard.hubLocalTimeNotice')}
                </p>
            </div>

            {hubId == null ? (
                <Empty description={t('wms.delivery.inhubDashboard.noHubSelected')} />
            ) : activeTabKey === 'sorting' ? (
                <SortingManagementTab hubId={hubId} rangeStart={rangeStart} rangeEnd={rangeEnd} isTailHub={isTailHub} />
            ) : (
                <TransportManagementTab hubId={hubId} rangeStart={rangeStart} rangeEnd={rangeEnd} />
            )}
        </div>
    );
};

export default InhubDashboardPage;
